import React, { useEffect, useState } from "react";

import SmartRtpLayout from "./components/SmartRtpLayout";
import SmartRtpCards from "./components/SmartRtpCards";
import {
  getHourlyReport,
  getGameWiseReport,
  getPoolReconciliation,
  getRtpVariance,
} from "../../../controllers/V1/smartRtp/smartRtpController";
import { formatSmartRtpNumber } from "../../../utils/bigMath";

export default function SmartRtpAnalyticsPage() {
  const [cards, setCards] = useState([]);
  const [rows, setRows] = useState([]);

  const loadAnalytics = async () => {
    const [hourly, gameWise, poolRecon, variance] = await Promise.all([
      getHourlyReport({}),
      getGameWiseReport({}),
      getPoolReconciliation({}),
      getRtpVariance({}),
    ]);

    const hourlyMetrics = hourly?.result?.metrics || {};
    const varianceMetrics = variance?.result?.metrics || {};

    setCards([
      {
        key: "hourly-orders",
        label: "Hourly Orders",
        value: hourlyMetrics.total_orders || 0,
      },
      {
        key: "hourly-stake",
        label: "Hourly Stake",
        value: formatSmartRtpNumber(hourlyMetrics.total_stake || 0),
      },
      {
        key: "actual-rtp",
        label: "Actual RTP %",
        value: formatSmartRtpNumber(
          varianceMetrics.actual_rtp ?? hourlyMetrics.actual_rtp ?? 0
        ),
        helper: "Excludes Big Prize & Time Slot awards",
      },
      {
        key: "variance",
        label: "RTP Variance",
        value: formatSmartRtpNumber(variance?.result?.variance || 0),
        helper: "vs target · scheduled big prizes excluded",
      },
      {
        key: "pool-outflow",
        label: "Pool Outflow",
        value: formatSmartRtpNumber(poolRecon?.result?.outflow || 0),
        helper: "RTP rewards only (pool debit)",
      },
      {
        key: "scheduled-bp-count",
        label: "Scheduled Big Prizes",
        value: hourlyMetrics.scheduled_big_prize_count || 0,
        helper: "Big Prize & Time Slot · this hour",
      },
      {
        key: "scheduled-bp-amount",
        label: "Scheduled BP Amount",
        value: formatSmartRtpNumber(hourlyMetrics.scheduled_big_prize_amount || 0),
        helper: "Not in RTP / not from pool balance",
      },
      {
        key: "rtp-rewards",
        label: "RTP Rewards",
        value: formatSmartRtpNumber(hourlyMetrics.total_rewards || 0),
      },
    ]);

    setRows(Array.isArray(gameWise?.result) ? gameWise.result : []);
  };

  useEffect(() => {
    document.title = "Crown King || Smart RTP Analytics";
    loadAnalytics();
  }, []);

  return (
    <SmartRtpLayout
      title="Smart RTP Analytics"
      activeKey="analytics"
      headerVariant="module"
      onRefresh={loadAnalytics}
    >
      <SmartRtpCards cards={cards} />
      <div className="card shadow mb-4">
        <div className="card-body pt-0">
          <p className="text-muted small mb-3">
            Game-wise (last 7 days). Scheduled Big Prize &amp; Time Slot wins are listed
            separately and do not affect pool RTP / Current Balance.
          </p>
          <div className="table-responsive">
            <table className="table table-bordered" width="100%">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Game</th>
                  <th>Orders</th>
                  <th>Stake</th>
                  <th>Wins</th>
                  <th>Scheduled Big Prizes</th>
                  <th>Scheduled BP Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => (
                    <tr key={row?._id || index}>
                      <td>{index + 1}</td>
                      <td>{row?.game_code || "-"}</td>
                      <td>{row?.orders || 0}</td>
                      <td>{formatSmartRtpNumber(row?.stake || 0)}</td>
                      <td>{row?.wins || 0}</td>
                      <td>{row?.scheduled_big_prize_count || 0}</td>
                      <td>{formatSmartRtpNumber(row?.scheduled_big_prize_amount || 0)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="img-center">
                      No analytics data found !
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SmartRtpLayout>
  );
}
